import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const RULES_FADE_DURATION_MS = 320;
const RULES_REVEAL_DELAY_MS = 40;

const GlowLine = () => (
  <div style={{
    width: '97%',
    height: '1px',
    margin: '0 auto',
    background: 'linear-gradient(90deg, rgba(14, 133, 120, 0) 0%, #14B8A6 50%, rgba(9, 82, 74, 0) 100%)',
    boxShadow: '0px 4px 4px rgba(20, 184, 166, 0.40)',
  }} />
);

interface RuleSection {
  icon: string;
  label: string;
  description: string;
}

function RulesCard({ mode }: { mode: 'short' | 'long' }) {
  const isLong = mode === 'long';
  const title = isLong ? 'Extended Trial Rules' : 'Trial Rules';

  const sections: RuleSection[] = isLong
    ? [
        {
          icon: '/icons/score.png',
          label: 'Score',
          description: 'Increases with correct answers. Each year gets harder, survive and score the highest on the leaderboard!',
        },
        {
          icon: '/icons/timer.png',
          label: 'Timer',
          description: 'Answer within 50 seconds for a Score bonus. The faster you are, the more points you win.',
        },
        {
          icon: '/icons/heart.png',
          label: 'Vitality',
          description: 'Your health percentage. Gain it for correct answers, lose it for wrong ones. The game ends when it runs out.',
        },
      ]
    : [
        {
          icon: '/icons/score.png',
          label: 'Score',
          description: 'Increases with correct answers. Score the highest!',
        },
        {
          icon: '/icons/timer.png',
          label: 'Timer',
          description: 'Answer within 50 seconds for a Score bonus. The faster you are, the more points you win.',
        },
      ];

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        background: 'rgba(46, 58, 102, 0.85)',
        borderRadius: 'clamp(16px, 2.5vw, 30px)',
        boxShadow: '0px 4px 4px rgba(0,0,0,0.25)',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <GlowLine />

      <div
        style={{
          textAlign: 'center',
          color: '#EAF2FF',
          fontSize: 'clamp(28px, 4vw, 70px)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontWeight: 400,
          textShadow: '0px 4px 37px rgba(95, 208, 255, 0.70)',
          padding: 'clamp(10px, 2vw, 28px) 0 clamp(8px, 1.5vw, 20px)',
          lineHeight: 1.1,
        }}
      >
        {title}
      </div>

      <GlowLine />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'flex-start',
          padding: 'clamp(16px, 3vw, 40px) clamp(12px, 2vw, 30px)',
          gap: 'clamp(8px, 2vw, 24px)',
        }}
      >
        {sections.map((section) => (
          <div
            key={section.label}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'clamp(6px, 1vw, 14px)',
            }}
          >
            <img
              src={section.icon}
              alt={section.label}
              style={{
                width: 'clamp(18px, 3vw, 36px)',
                height: 'clamp(18px, 3vw, 36px)',
                objectFit: 'contain',
              }}
            />
            <div
              style={{
                color: '#A5FFF5',
                fontSize: 'clamp(14px, 2vw, 24px)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              {section.label}
            </div>
            <div
              style={{
                color: 'white',
                fontSize: 'clamp(11px, 1.5vw, 20px)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 400,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {section.description}
            </div>
          </div>
        ))}
      </div>

      <GlowLine />
    </div>
  );
}

export default function GameRulesScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [isVisible, setIsVisible] = useState(false);
  const [isBlackOverlayVisible, setIsBlackOverlayVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  const mode = searchParams.get("mode") === "long" ? "long" : "short";
  const buttonLabel = mode === "long" ? "Begin Extended Trial" : "Begin Trial";

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsVisible(true);
      setIsBlackOverlayVisible(false);
    }, RULES_REVEAL_DELAY_MS);
    return () => clearTimeout(revealTimer);
  }, []);

  const handleBeginTrial = () => {
    if (isLeaving) return;
    setIsLeaving(true);
    setIsVisible(false);
    setIsBlackOverlayVisible(true);
    window.setTimeout(() => {
      const query = searchParams.toString();
      if (mode === 'long') {
        // Long mode keeps the "Year 1" intro — it frames the 10-year run.
        navigate(`/game/play/${mode}?${query}`);
        return;
      }
      // Quick & custom games go straight into the session, no Year 1 intro.
      const sessionId = searchParams.get('session_id') || crypto.randomUUID();
      navigate(`/game/play/${mode}/${sessionId}?${query}`);
    }, RULES_FADE_DURATION_MS);
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center overflow-hidden px-4"
      style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
    >

      {/* Main card */}
      <div
        className="relative w-full max-w-4xl px-6 py-5 md:px-8 md:py-6 flex flex-col items-center gap-5"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${RULES_FADE_DURATION_MS}ms ease-in-out`,
        }}
      >
        <div className="relative w-full">
          <RulesCard mode={mode} />

          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={handleBeginTrial}
              disabled={isLeaving}
              className="px-8 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Fade overlay */}
      <div
        className="pointer-events-none absolute inset-0 bg-black"
        style={{
          opacity: isBlackOverlayVisible ? 1 : 0,
          transition: `opacity ${RULES_FADE_DURATION_MS}ms ease-in-out`,
        }}
      />
    </div>
  );
}
